import React, { useState, useRef } from 'react';
import { useFormikContext } from 'formik';
import KoobInputSelect from './KoobInputSelect';
import KoobInputLabel from './KoobInputLabel';

export interface KoobSelectLanguageFieldProps {
  name: string;
  label: string;
  required?: boolean;
  useTitleAsLabel?: boolean;
  getLanguages: () => Promise<any>;
  defaultLocale: any;
  disabled?: boolean;
  tooltipDescription?: React.ReactNode;
}

const KoobSelectLanguageField: React.FC<KoobSelectLanguageFieldProps> = ({
  name,
  label,
  required = false,
  useTitleAsLabel = false,
  getLanguages,
  defaultLocale,
  disabled = false,
  tooltipDescription,
}) => {
  const { values, setFieldValue, errors } =
    useFormikContext<Record<string, any>>();
  const [languages, setLanguages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const hasFetched = useRef(false);

  if (!hasFetched.current) {
    hasFetched.current = true;
    getLanguages().then((res) => {
      setLoading(false);
      const mapped = (res?.data || []).map(({ name, description, title }) => ({
        label: useTitleAsLabel ? title : name.toUpperCase(),
        value: name,
        alpha2: description.toLowerCase(),
      }));
      setLanguages(mapped);
      // Normalize to the { value, label, alpha2 } object shape that the rest
      // of the form (and downstream `values.locale.value`) expects.
      // If the Formik field is already a string (loaded from tripFilters or
      // localStorage cache), the old `!values[name]` guard would skip
      // normalization and submit would dereference `.value` on a string,
      // producing undefined.
      const formikValue = values[name];
      const isAlreadyNormalized =
        formikValue && typeof formikValue === 'object';
      if (!isAlreadyNormalized) {
        const stringValue =
          typeof formikValue === 'string'
            ? formikValue
            : (defaultLocale?.value ?? defaultLocale);
        if (stringValue) {
          const option = mapped.find((l: any) => l.value === stringValue);
          if (option) {
            setFieldValue(name, option);
          }
        }
      }
    });
  }

  const currentValue =
    typeof values[name] === 'object' ? values[name]?.value : values[name];

  return (
    <KoobInputLabel
      label={label}
      required={required}
      tooltipDescription={tooltipDescription}
    >
      <KoobInputSelect
        options={languages}
        loading={loading}
        value={currentValue ?? defaultLocale}
        setValue={(value) => {
          const option = languages.find((l) => l.value === value);
          setFieldValue(name, option ?? value);
        }}
        error={Boolean(errors[name])}
        data-testid={name}
        renderOption={({ label, alpha2 }: any) => (
          <div className="flex items-center gap-1.5">
            <span
              className={`flag-icon flag-icon-${alpha2} h-4 w-5 shrink-0 rounded-sm`}
            />
            <span>{label}</span>
          </div>
        )}
        renderValue={({ label, alpha2 }: any) => (
          <div className="flex items-center gap-1.5">
            <span
              className={`flag-icon flag-icon-${alpha2} h-4 w-5 shrink-0 rounded-sm`}
            />
            <span>{label}</span>
          </div>
        )}
        disabled={disabled}
        searchable
      />
    </KoobInputLabel>
  );
};

export default KoobSelectLanguageField;
